import { Box, type SxProps, type Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { ACCENT_BLUE, DAY_TOGGLE, FONT_SIZE, INK, MOTION, SURFACE } from "../../../theme/tokens";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";
import { chipColorSx, chipGeometrySx, chipLookColors, chipTypeSx, type DayToggleLook, type DayToggleToneColors } from "./dayToggleChipSx";
import { ReadOnlyDayChip } from "./ReadOnlyDayChip";

export type DayToggleSize = "xsmall" | "small" | "medium" | "large";

/** Chip edge (px) per size. */
const CELL_SIZE: Record<DayToggleSize, number> = {
	xsmall: DAY_TOGGLE.CELL_XS,
	small: DAY_TOGGLE.CELL_SM,
	medium: DAY_TOGGLE.CELL_MD,
	large: DAY_TOGGLE.CELL_LG,
};

/** A touch screen: where `touchSize` replaces `size`. */
const COARSE_POINTER = "@media (pointer: coarse)";

/** Gap (px) between chips: tighter at `xsmall`, so its week stays a compact block. */
const chipGap = (size: DayToggleSize): number => (size === "xsmall" ? DAY_TOGGLE.CHIP_GAP_XS : DAY_TOGGLE.CHIP_GAP);

/** One day: a short label, an on/off state, and an optional corner marker. */
export type DayToggleCell = {
	/** Stable identity handed back to `onToggle` — a grid cell key, an ISO date, a weekday index. */
	key: string | number;
	/** The glyph inside the chip (a day initial). Kept to 1–3 characters by the chip's width. */
	label: string;
	selected: boolean;
	/** The chip's accessible name — the visible label is an initial, so this carries the real meaning. */
	ariaLabel: string;
	/**
	 * Corner status marker. `color` is passed in (a semantic tone from the caller's own scale) so the
	 * marker can never disagree with whatever surface it is annotating.
	 *
	 * `ringColor` overrides the hairline drawn around the dot. It defaults to the page surface, which
	 * separates the dot from a filled chip — right for a dark marker, but not enough on its own for a
	 * light one (a saturated yellow dot is ~1.6:1 on white however cleanly it is separated). A caller
	 * whose scale HAS a dark counterpart to its marker should pass it here, so a light marker keeps a
	 * legible edge on every chip state instead of dissolving into the pale ones.
	 */
	marker?: { color: string; ringColor?: string; tooltip?: string };
	/**
	 * Status wash for the cell while it is UNSELECTED — the caller's own scale, so a day that needs
	 * attention reads as that scale's colour rather than as a neutral chip with a dot on it.
	 *
	 * Unselected only, deliberately: selection is the chip's primary state and has to stay unambiguous
	 * down a whole week. A cell that is both washed and selected shows the selected fill, and its marker
	 * is what carries the status through.
	 */
	tone?: { bg: string; ink: string; border?: string };
	disabled?: boolean;
	/**
	 * Already translated: the chip's hover text — e.g. why a disabled chip can't be pressed. A `marker.tooltip` takes its
	 * place. A chip that is both `disabled` and has a tooltip stays focusable (`aria-disabled`, its presses ignored) so
	 * the reason also shows on keyboard focus; a disabled chip without one is a plain `disabled` button. The `ariaLabel`
	 * should say the same for a screen reader.
	 */
	tooltip?: string;
	dataTestId?: string;
};

/** A cluster of days rendered together — one per schedule week, with an optional caption. */
export type DayToggleGroup = {
	key: string | number;
	/** Small caption above the cluster (a week number). Omit for an uncaptioned strip. */
	label?: string;
	cells: DayToggleCell[];
};

type BaseProps = {
	groups: DayToggleGroup[];
	/**
	 * Fill of a selected chip: `primary` follows the org's brand colour, `success` marks a
	 * confirmed/settled pattern, and `accent` is the fixed `ACCENT_BLUE` for a surface whose design
	 * specifies that blue rather than the configurable brand.
	 */
	tone?: "primary" | "success" | "accent";
	/**
	 * Chip edge. `xsmall` is for a read-only strip repeated down a dense table (a week per line, several
	 * lines per row), where even the small chip makes each line taller than its neighbours' text.
	 */
	size?: DayToggleSize;
	/**
	 * `square` (the default) draws rounded squares, which read as one week block. `circle` draws round
	 * chips — for a surface whose design specifies them, typically at `xsmall` beside other round pills.
	 */
	shape?: "square" | "circle";
	/**
	 * Let each cluster fill its container instead of drawing fixed squares: the cells become equal
	 * fractions of one row and grow wide enough for a real word ("Sun", not "S").
	 *
	 * The strip form exists to sit in a dense table row, where a square is right and the width has to be
	 * predictable (see `dayToggleStripWidth`). A standalone form is the other case — one week per line,
	 * full day names, the width of the panel — and that is what this is for. Only the cell geometry
	 * changes; the state, the markers and the a11y are the same control.
	 */
	stretch?: boolean;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/** The control: every chip is a `<button aria-pressed>` and a click hands the cell to `onToggle`. */
type InteractiveProps = BaseProps & {
	onToggle: (cell: DayToggleCell) => void;
	/**
	 * How an unselected day rests. `muted` (the default) is a quiet grey chip for a dense row, where the
	 * selected days are the only thing meant to register. `outlined` is a white chip on a field's outline
	 * with readable ink and body-size text, and a selected day is a soft brand tint rather than a solid
	 * fill — for a standalone form where an off day is still a live choice and must not read as disabled
	 * next to the form's other inputs, and a full week on must not read as one heavy block.
	 */
	look?: DayToggleLook;
	readOnly?: false;
	ariaLabel?: never;
	/**
	 * The chips' size on a touch screen (`pointer: coarse`), where it differs from `size`: an `xsmall` strip of buttons
	 * sized for a mouse is too small a target for a finger, so a dense form can draw 20px chips on a laptop and grow
	 * them on a phone. Absent, the chips are `size` on every pointer. `dayToggleStripWidth` gives the `size` width.
	 */
	touchSize?: DayToggleSize;
	/**
	 * Every chip's tab stop: `-1` for a grid that moves focus between its cells itself (roving tabindex), so the strip
	 * isn't seven extra Tab presses on every row. Absent, the chips are in the natural tab order.
	 */
	chipTabIndex?: 0 | -1;
	/**
	 * Already translated: wraps the chips in `role="group"` with this accessible name ("Days for 2 people"), so a strip
	 * that sits among other strips — one per line of a table row — says which line its days belong to. Absent, the
	 * strip is unnamed and each chip stands on its own name.
	 */
	groupAriaLabel?: string;
};

/**
 * A display of a pattern that is not editable HERE (a table row summarising a shift's days): the chips
 * are plain, unfocusable glyphs drawn at full ink — selected days keep the selected fill, and a cell's
 * `disabled` no longer fades it, since nothing on the strip is operable. The strip is announced as ONE
 * image named by `ariaLabel` (e.g. "Runs Mon, Tue, Fri"), because seven initials read one by one say
 * nothing; the per-cell `ariaLabel`s are not announced in this mode.
 */
type ReadOnlyProps = BaseProps & {
	readOnly: true;
	/** Already translated: the whole strip's accessible name — which days are on. */
	ariaLabel: string;
	onToggle?: never;
	look?: never;
	chipTabIndex?: never;
	groupAriaLabel?: never;
};

type Props = InteractiveProps | ReadOnlyProps;

/** Width (px) of one week cluster, so a hoisted caption can sit exactly over its chips. */
const clusterWidth = (cellCount: number, cellSize: number, gap: number): number => cellCount * cellSize + Math.max(0, cellCount - 1) * gap;

/**
 * Total width (px) of the whole strip.
 *
 * Exported because a LIST of these only lines up if the strip is a fixed-width column: CSS grid sizes
 * `auto` columns per grid container, so sibling rows that are each their own grid resolve different
 * column widths and the strips end up staggered. Pinning the column to this value is what makes one
 * hoisted caption header correct for every row (and avoids depending on `subgrid`).
 */
export const dayToggleStripWidth = (groups: DayToggleGroup[], size: "xsmall" | "small" | "medium" = "small"): number => {
	const cellSize = CELL_SIZE[size];
	const clusters = groups.reduce((total, group) => total + clusterWidth(group.cells.length, cellSize, chipGap(size)), 0);
	return clusters + Math.max(0, groups.length - 1) * DAY_TOGGLE.CLUSTER_GAP;
};

const GROUP_LABEL_SX = {
	fontSize: FONT_SIZE.LABEL_MICRO,
	fontWeight: 700,
	letterSpacing: "0.08em",
	textTransform: "uppercase" as const,
	color: INK.DISABLED,
	lineHeight: 1.2,
};

/** The corner status dot. */
const renderMarker = (marker: NonNullable<DayToggleCell["marker"]>) => (
	<Box
		component="span"
		aria-hidden
		sx={{
			position: "absolute",
			top: DAY_TOGGLE.MARKER_OFFSET,
			right: DAY_TOGGLE.MARKER_OFFSET,
			width: DAY_TOGGLE.MARKER,
			height: DAY_TOGGLE.MARKER,
			borderRadius: "50%",
			backgroundColor: marker.color,
			// DEFAULT: no `ringColor` → the page surface, the original behaviour.
			border: `${DAY_TOGGLE.MARKER_RING}px solid ${marker.ringColor ?? SURFACE}`,
			"@keyframes dayToggleMarkerPop": {
				from: { opacity: 0, transform: "scale(0.6)" },
				to: { opacity: 1, transform: "scale(1)" },
			},
			animation: `dayToggleMarkerPop ${MOTION.DURATION.POP}ms ${MOTION.EASE_OUT}`,
			"@media (prefers-reduced-motion: reduce)": { animation: "none" },
		}}
	/>
);

/**
 * A grid of on/off day chips, grouped into clusters — the "which days does this pattern cover?" control.
 *
 * Each chip is a real `<button aria-pressed>` so the whole strip is keyboard- and screen-reader-operable,
 * and each may carry a corner marker for a day that still needs attention. Generic over what a day means:
 * the caller owns the cell keys, the labels, the grouping (one cluster per schedule week is the common
 * case), and the marker's colour. For a single either/or use `SystemFlatSwitch`; for selectable value
 * chips use `SystemChips`.
 *
 * `readOnly` turns the same strip into a display (see `ReadOnlyProps`): same squares, same fills, no
 * buttons, one accessible name for the whole strip. Captions (`DayToggleGroup.label`) are drawn but sit
 * inside that image, so a multi-week strip's `ariaLabel` should name the weeks itself.
 */
export const DayToggleGrid: FC<Props> = (props) => {
	// DEFAULT: `shape` square — the look every existing caller already has.
	const { groups, tone = "primary", size = "small", shape = "square", stretch = false, look = "muted", dataTestId, sx } = props;
	const cellSize = CELL_SIZE[size];
	const gap = chipGap(size);
	const touchSize = props.readOnly ? undefined : props.touchSize;
	// DEFAULT: no `chipTabIndex` leaves each chip in the natural tab order; no `groupAriaLabel` leaves the strip unnamed —
	// the interactive strip as every existing caller already has it.
	const chipTabIndex = props.readOnly ? undefined : props.chipTabIndex;
	const groupAriaLabel = props.readOnly ? undefined : props.groupAriaLabel;
	// Fixed squares only: under `stretch` the column already owns the width, and the height follows `size`.
	const touchChipSx =
		touchSize === undefined || stretch ? {} : { [COARSE_POINTER]: { ...chipGeometrySx(CELL_SIZE[touchSize], false, shape), ...chipTypeSx(touchSize, look) } };
	const touchGapSx = touchSize === undefined ? {} : { [COARSE_POINTER]: { gap: `${chipGap(touchSize)}px` } };
	// One place resolves the tone, so the chip fill, its hover, its focus ring and the `accent` opt-out
	// of the brand palette can't drift apart.
	const toneColors = (theme: Theme): DayToggleToneColors =>
		tone === "accent"
			? { main: ACCENT_BLUE.MAIN, dark: ACCENT_BLUE.DARK, contrastText: ACCENT_BLUE.ON_ACCENT }
			: { main: theme.palette[tone].main, dark: theme.palette[tone].dark, contrastText: theme.palette[tone].contrastText };

	return (
		<Box
			data-testid={dataTestId}
			// Read-only: one named image, so the strip is a single stop that says what it shows. Interactive
			// strips carry nothing here — each chip is its own named button — unless the caller names the
			// group, which then announces once as the strip is entered.
			role={props.readOnly ? "img" : groupAriaLabel === undefined ? undefined : "group"}
			aria-label={props.readOnly ? props.ariaLabel : groupAriaLabel}
			sx={[
				stretch
					? { display: "flex", flexDirection: "column", gap: `${DAY_TOGGLE.CLUSTER_GAP}px` }
					: { display: "flex", alignItems: "flex-end", flexWrap: "wrap", gap: `${DAY_TOGGLE.CLUSTER_GAP}px` },
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			{groups.map((group) => (
				<Box key={group.key} sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
					{group.label && (
						<Box component="span" sx={[GROUP_LABEL_SX, { pl: "1px" }]}>
							{group.label}
						</Box>
					)}
					<Box
						sx={
							stretch
								? { display: "grid", gridTemplateColumns: `repeat(${group.cells.length}, minmax(0, 1fr))`, gap: `${gap}px`, ...touchGapSx }
								: { display: "flex", gap: `${gap}px`, ...touchGapSx }
						}
					>
						{group.cells.map((cell) => {
							const marker = cell.marker && renderMarker(cell.marker);
							// A disabled chip that says why stays in reach of the keyboard: not `disabled`, which would drop
							// it from the tab order and keep its tooltip from ever showing on focus.
							const explainsDisabled = cell.disabled === true && cell.tooltip !== undefined;
							const chip = props.readOnly ? (
								<ReadOnlyDayChip key={cell.key} cell={cell} cellSize={cellSize} stretch={stretch} shape={shape} size={size} toneColors={toneColors}>
									{marker}
								</ReadOnlyDayChip>
							) : (
								<Box
									component="button"
									type="button"
									key={cell.key}
									disabled={explainsDisabled ? undefined : cell.disabled}
									aria-disabled={explainsDisabled ? true : undefined}
									tabIndex={chipTabIndex}
									aria-pressed={cell.selected}
									aria-label={cell.ariaLabel}
									data-testid={cell.dataTestId}
									onClick={() => {
										if (!explainsDisabled) props.onToggle(cell);
									}}
									sx={(theme) => {
										const colors = toneColors(theme);
										const lookColors = chipLookColors(colors, look);
										return {
											...chipGeometrySx(cellSize, stretch, shape),
											// A cell's own `tone` only applies while it is unselected — see `DayToggleCell.tone`.
											...chipColorSx(cell, colors, look),
											...chipTypeSx(size, look),
											...touchChipSx,
											cursor: cell.disabled ? "default" : "pointer",
											opacity: cell.disabled ? 0.5 : 1,
											transition: theme.transitions.create(["background-color", "border-color", "color", "transform"], {
												duration: MOTION.DURATION.CONTROL,
												easing: MOTION.EASE_OUT,
											}),
											"@media (hover: hover) and (pointer: fine)": {
												"&:hover:not(:disabled):not([aria-disabled='true'])": {
													borderColor: colors.main,
													backgroundColor: cell.selected ? (look === "outlined" ? lookColors.selectedBg : colors.dark) : alpha(colors.main, 0.1),
													// A toned cell keeps its own ink through the hover wash, which is the accent's
													// tint — the status colour is the point of the cell, not a resting decoration.
													color: cell.selected ? lookColors.selectedInk : (cell.tone?.ink ?? lookColors.restInk),
												},
											},
											"&:active:not(:disabled):not([aria-disabled='true'])": { transform: "scale(0.94)" },
											"&:focus-visible": { outline: `2px solid ${colors.main}`, outlineOffset: 1 },
											"@media (prefers-reduced-motion: reduce)": {
												transition: "none",
												"&:active:not(:disabled):not([aria-disabled='true'])": { transform: "none" },
											},
										};
									}}
								>
									{cell.label}
									{marker}
								</Box>
							);
							if (cell.marker?.tooltip) {
								return (
									<InHouseTooltip key={cell.key} title={cell.marker.tooltip}>
										{chip}
									</InHouseTooltip>
								);
							}
							return cell.tooltip ? (
								<InHouseTooltip key={cell.key} title={cell.tooltip}>
									<Box component="span" sx={{ display: "flex", minWidth: 0 }}>
										{chip}
									</Box>
								</InHouseTooltip>
							) : (
								chip
							);
						})}
					</Box>
				</Box>
			))}
		</Box>
	);
};

export default DayToggleGrid;
